import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';
import { artistInfo } from '../../data/artworksData';

const Header = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Check if on home page (for transparent header)
  const isHomePage = location.pathname === '/';

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { path: '/', label: { ru: 'Главная', en: 'Home' } },
    { path: '/gallery', label: { ru: 'Галерея', en: 'Gallery' } },
    { path: '/about', label: { ru: 'О художнике', en: 'About' } },
    { path: '/contact', label: { ru: 'Контакты', en: 'Contact' } }
  ];


  // Determine header style based on scroll and page
  const headerBg = isHomePage && !isScrolled
    ? 'bg-transparent'
    : 'bg-white border-b border-gallery-border';

  const textColor = isHomePage && !isScrolled
    ? 'text-white'
    : 'text-gallery-text';

  const logoColor = isHomePage && !isScrolled
    ? 'text-white'
    : 'text-gallery-text';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className={`font-serif text-xl md:text-2xl tracking-wide transition-colors ${logoColor}`}>
              {artistInfo.name[lang].split(' ')[0]}
              <span className="font-light ml-2 hidden sm:inline">
                {artistInfo.name[lang].split(' ').slice(1).join(' ')}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm tracking-wider uppercase transition-colors hover:text-gallery-accent ${
                    location.pathname === link.path
                      ? 'text-gallery-accent'
                      : textColor
                  }`}
                >
                  {link.label[lang]}
                </Link>
              ))}

              {/* Language Switcher - Touch-friendly */}
              <div className="relative">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className={`flex items-center justify-center gap-2 min-h-[44px] px-3 text-sm tracking-wider uppercase transition-colors ${textColor} hover:text-gallery-accent`}
                  aria-label="Change language"
                  aria-expanded={showLangMenu}
                >
                  <Globe size={16} />
                  {lang.toUpperCase()}
                </button>

                <AnimatePresence>
                  {showLangMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-2 bg-white shadow-lg border border-gallery-border min-w-[120px] z-50"
                    >
                      <button
                        onClick={() => { i18n.changeLanguage('ru'); setShowLangMenu(false); }}
                        className={`w-full px-4 py-3 min-h-[44px] text-sm text-left hover:bg-gallery-bg transition-colors ${lang === 'ru' ? 'text-gallery-accent' : 'text-gallery-text'}`}
                      >
                        Русский
                      </button>
                      <button
                        onClick={() => { i18n.changeLanguage('en'); setShowLangMenu(false); }}
                        className={`w-full px-4 py-3 min-h-[44px] text-sm text-left hover:bg-gallery-bg transition-colors ${lang === 'en' ? 'text-gallery-accent' : 'text-gallery-text'}`}
                      >
                        English
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button - Touch-friendly 44px target */}
            <button
              className={`md:hidden flex items-center justify-center w-11 h-11 -mr-2 transition-colors ${textColor} active:opacity-70`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white z-40 md:hidden"
          >
            <div className="container mx-auto px-4 pt-24 pb-8">
              <nav className="flex flex-col space-y-6">
                {navLinks.map((link, idx) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className={`block font-serif text-2xl transition-colors ${
                        location.pathname === link.path
                          ? 'text-gallery-accent'
                          : 'text-gallery-text hover:text-gallery-accent'
                      }`}
                    >
                      {link.label[lang]}
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile Language Switcher - Touch-friendly */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.1 }}
                  className="pt-6 border-t border-gallery-border"
                >
                  <p className="text-gallery-muted text-sm uppercase tracking-wider mb-4">
                    {lang === 'ru' ? 'Язык' : 'Language'}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => i18n.changeLanguage('ru')}
                      className={`min-h-[48px] px-6 py-3 text-sm transition-colors active:scale-[0.98] ${
                        lang === 'ru'
                          ? 'bg-gallery-text text-white'
                          : 'border border-gallery-border text-gallery-text'
                      }`}
                    >
                      Русский
                    </button>
                    <button
                      onClick={() => i18n.changeLanguage('en')}
                      className={`min-h-[48px] px-6 py-3 text-sm transition-colors active:scale-[0.98] ${
                        lang === 'en'
                          ? 'bg-gallery-text text-white'
                          : 'border border-gallery-border text-gallery-text'
                      }`}
                    >
                      English
                    </button>
                  </div>
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
